import { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";
import { useToast } from "../ToastContext";

export default function BrandKit({ user, onApply }) {
  const { addToast } = useToast();
  const [kit, setKit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    supabase.from("brand_kits").select("*").eq("user_id", user.id).maybeSingle()
      .then(({ data }) => { if (data) setKit(data); });
  }, [user?.id]);

  const handleSave = async (currentSettings) => {
    setSaving(true);
    const { error } = await supabase.from("brand_kits").upsert({
      user_id: user.id,
      ...currentSettings,
      updated_at: new Date().toISOString()
    }, { onConflict: "user_id" });
    if (!error) {
      setKit({ ...kit, ...currentSettings });
      addToast("Brand kit saved!", "success");
    }
    setSaving(false);
  };

  const handleApply = () => {
    if (kit) {
      onApply(kit);
      setOpen(false);
      addToast("Brand kit applied!", "success");
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-[10px] font-black uppercase text-gray-400 hover:bg-white/10 transition-all"
      >
        🎨 Brand Kit
      </button>

      {open && (
        <div className="absolute top-full mt-2 right-0 w-72 bg-slate-900 border border-white/10 rounded-2xl p-4 z-50 space-y-4 shadow-2xl">
          <div className="flex justify-between items-center">
            <p className="text-[10px] font-black uppercase text-white tracking-widest">Brand Kit</p>
            <button onClick={() => setOpen(false)} className="text-gray-500 text-sm">✕</button>
          </div>

          {kit ? (
            <>
              <div className="flex gap-2 items-center">
                <div className="w-6 h-6 rounded-full border border-white/20" style={{ background: kit.dots_color }} />
                <div className="w-6 h-6 rounded-full border border-white/20" style={{ background: kit.bg_color }} />
                <div className="w-6 h-6 rounded-full border border-white/20" style={{ background: kit.eyes_color }} />
                <span className="text-[10px] text-gray-500 uppercase font-black ml-2">{kit.dots_type}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleApply}
                  className="py-2.5 bg-blue-600 hover:bg-blue-500 rounded-xl text-[10px] font-black uppercase text-white transition-all"
                >
                  Apply to QR
                </button>
                <button
                  onClick={() => addToast("Edit your style then click Save Kit", "success")}
                  className="py-2.5 bg-white/5 hover:bg-white/10 rounded-xl text-[10px] font-black uppercase text-gray-400 transition-all"
                >
                  Edit
                </button>
              </div>
            </>
          ) : (
            <p className="text-[10px] text-gray-500">No brand kit saved yet. Style your QR then save it as a kit.</p>
          )}

          <button
            onClick={() => handleSave({
              dots_color: document.querySelector('[data-kit="dots"]')?.value,
              bg_color: document.querySelector('[data-kit="bg"]')?.value,
              eyes_color: document.querySelector('[data-kit="eyes"]')?.value,
              dots_type: document.querySelector('[data-kit="dotsType"]')?.value,
              corners_type: document.querySelector('[data-kit="cornersType"]')?.value,
            })}
            disabled={saving}
            className="w-full py-2.5 bg-green-600/20 border border-green-500/30 text-green-400 rounded-xl text-[10px] font-black uppercase hover:bg-green-600 hover:text-white transition-all"
          >
            {saving ? "Saving..." : "💾 Save Current Style as Kit"}
          </button>
        </div>
      )}
    </div>
  );
}